import React, { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { TYPO, R, S, SHADCN } from '../theme';
import IconButton from './IconButton';

// ── <DayPicker> — petit calendrier en popover pour sauter à une date.
// Complète les flèches jour précédent / suivant : atteindre un samedi d'il y a
// trois semaines demandait vingt clics. Dates au format 'YYYY-MM-DD'.
//
// `max` borne la sélection (par défaut aujourd'hui) : l'onglet Aujourd'hui ne
// sait rien afficher du futur, les jours au-delà sont donc désactivés.

const MOIS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const JOURS_COURTS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayISO = () => iso(new Date());

export default function DayPicker({ value, max = todayISO(), onChange, label = 'Choisir une date' }) {
  const [open, setOpen] = useState(false);
  // Mois affiché dans le calendrier, indépendant de la date sélectionnée : on
  // peut feuilleter septembre puis renoncer sans rien changer.
  const [curseur, setCurseur] = useState(() => (value || todayISO()).slice(0, 7));
  const wrap = useRef(null);

  // À l'ouverture, on retombe toujours sur le mois de la date sélectionnée.
  useEffect(() => { if (open) setCurseur((value || todayISO()).slice(0, 7)); }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onClick = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, [open]);

  const [annee, mois] = curseur.split('-').map(Number);
  const premier = new Date(annee, mois - 1, 1);
  const nbJours = new Date(annee, mois, 0).getDate();
  // getDay() renvoie 0 pour dimanche : on décale pour une semaine qui commence lundi.
  const decalage = (premier.getDay() + 6) % 7;
  const cases = [...Array(decalage).fill(null), ...Array.from({ length: nbJours }, (_, i) => i + 1)];

  const moisPrecedent = () => setCurseur(iso(new Date(annee, mois - 2, 1)).slice(0, 7));
  const moisSuivant   = () => setCurseur(iso(new Date(annee, mois, 1)).slice(0, 7));
  const moisSuivantBloque = `${annee}-${String(mois).padStart(2, '0')}` >= max.slice(0, 7);

  const choisir = (jour) => {
    const d = `${annee}-${String(mois).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
    if (d > max) return;
    onChange(d);
    setOpen(false);
  };

  return (
    <div ref={wrap} style={{ position: 'relative', display: 'inline-flex' }}>
      <IconButton aria-label={label} aria-expanded={open} onClick={() => setOpen(v => !v)}>
        <Calendar size={17} />
      </IconButton>

      {open && (
        <div
          role="dialog"
          aria-label={label}
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50,
            background: SHADCN.popover || SHADCN.cardSolid, border: `1px solid ${SHADCN.border}`,
            borderRadius: R.md, boxShadow: SHADCN.shadowLg, padding: S[3], width: 252,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: S[2] }}>
            <IconButton aria-label="Mois précédent" onClick={moisPrecedent}><ChevronLeft size={16} /></IconButton>
            <div style={{ ...TYPO.caption, color: SHADCN.fg, fontWeight: 600 }}>
              {MOIS_FR[mois - 1]} {annee}
            </div>
            <IconButton aria-label="Mois suivant" onClick={moisSuivant} disabled={moisSuivantBloque}>
              <ChevronRight size={16} />
            </IconButton>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {JOURS_COURTS.map((j, i) => (
              <div key={i} style={{ ...TYPO.eyebrow, fontSize: 9, color: SHADCN.mutedFg, textAlign: 'center', paddingBottom: 4 }}>{j}</div>
            ))}
            {cases.map((jour, i) => {
              if (!jour) return <div key={`v${i}`} />;
              const d = `${annee}-${String(mois).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
              const selectionne = d === value;
              const aujourdhui  = d === todayISO();
              const bloque      = d > max;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => choisir(jour)}
                  disabled={bloque}
                  aria-current={selectionne ? 'date' : undefined}
                  style={{
                    ...TYPO.caption, ...TYPO.num,
                    height: 30, border: aujourdhui && !selectionne ? `1px solid ${SHADCN.border}` : '1px solid transparent',
                    borderRadius: R.sm, cursor: bloque ? 'default' : 'pointer',
                    background: selectionne ? SHADCN.fg : 'transparent',
                    color: bloque ? SHADCN.border : selectionne ? SHADCN.bg : SHADCN.fg,
                    fontWeight: selectionne || aujourdhui ? 600 : 400,
                    transition: 'background 0.12s, color 0.12s',
                  }}
                  onMouseEnter={e => { if (!bloque && !selectionne) e.currentTarget.style.background = SHADCN.muted; }}
                  onMouseLeave={e => { if (!selectionne) e.currentTarget.style.background = 'transparent'; }}
                >
                  {jour}
                </button>
              );
            })}
          </div>

          {value !== max && (
            <button
              type="button"
              onClick={() => { onChange(max); setOpen(false); }}
              style={{
                ...TYPO.caption, width: '100%', marginTop: S[2], padding: '6px 0',
                background: 'transparent', border: `1px solid ${SHADCN.border}`,
                borderRadius: R.sm, color: SHADCN.mutedFg, cursor: 'pointer',
              }}
            >
              Revenir à aujourd'hui
            </button>
          )}
        </div>
      )}
    </div>
  );
}
